import { PAGE_LAYOUT_TYPE } from 'src/app/slice';

export interface SwitcherVisibility {
  hasMarketplaceSwitcher: boolean;
  hasRegionSwitcher: boolean;
  /**
   * True when the page EXPLICITLY opted into both controls. Reported as a
   * config error by callers that publish metrics.
   *
   * Deliberately excludes the implicit "marketplaceSwitcher omitted means on"
   * default: that is a legacy default rather than something the page asked
   * for, so treating it as a config error would fire this for every
   * region-switcher page.
   */
  isConflictingConfig: boolean;
}

export interface SwitcherVisibilityInput {
  /**
   * Whether the marketplace switcher is on, INCLUDING the legacy
   * "config omitted means on" default. Each caller supplies its own default
   * rule, which differ between the standard header and Trim.
   */
  marketplaceEnabled: boolean;
  /**
   * Whether the page opted into the marketplace switcher explicitly, i.e.
   * `useMarketplaceSwitcher === true`. Distinct from `marketplaceEnabled`,
   * which is also true when the config is simply absent.
   */
  marketplaceExplicitlyEnabled: boolean;
  regionSwitcherEnabled: boolean;
  pageLayoutType: PAGE_LAYOUT_TYPE;
}

/**
 * Resolves which of the two header location controls renders.
 *
 * The marketplace switcher and the region switcher occupy the same slot and
 * must never render together. Both flags are derived here, from one shared
 * decision, so they cannot contradict each other: `hasMarketplaceSwitcher`
 * is computed from `hasRegionSwitcher`, making the both-true state
 * unrepresentable.
 *
 * Precedence: the region switcher yields to an EXPLICIT marketplace opt-in
 * (`useMarketplaceSwitcher: true`), preserving the established bias towards
 * the marketplace switcher when a page genuinely requests both. It does NOT
 * yield to the implicit "marketplace config omitted means on" default, since
 * in that case `regionSwitcherEnabled` is the only switcher the page actually
 * asked for.
 */
export const resolveSwitcherVisibility = ({
  marketplaceEnabled,
  marketplaceExplicitlyEnabled,
  regionSwitcherEnabled,
  pageLayoutType,
}: SwitcherVisibilityInput): SwitcherVisibility => {
  const isRenderableLayout =
    pageLayoutType !== PAGE_LAYOUT_TYPE.FOCUS &&
    pageLayoutType !== PAGE_LAYOUT_TYPE.FOCUS_NO_HEADER;

  const hasRegionSwitcher =
    regionSwitcherEnabled &&
    !marketplaceExplicitlyEnabled &&
    isRenderableLayout;

  const hasMarketplaceSwitcher =
    marketplaceEnabled && !hasRegionSwitcher && isRenderableLayout;

  return {
    hasMarketplaceSwitcher,
    hasRegionSwitcher,
    isConflictingConfig: regionSwitcherEnabled && marketplaceExplicitlyEnabled,
  };
};
